import { Injectable, Inject, PLATFORM_ID, Renderer2, RendererFactory2 } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LoadingService } from './loading-service';

@Injectable({
  providedIn: 'root'
})
export class SafariStyleService {
  private renderer: Renderer2;
  private isBrowser: boolean;
  private metaThemeColor: HTMLMetaElement | null = null;
  private fixIosTop: HTMLDivElement | null = null;
  private fixIosBottom: HTMLDivElement | null = null;
  private originalBodyBgColor: string = '';
  private originalHtmlBgColor: string = '';
  private cachedIsMobileSafari: boolean | null = null;
  private cachedIosVersion: number | null = null;
  private cachedIsIOS: boolean | null = null;
  private cachedIsYandexBrowser: boolean | null = null;
  private cachedIsWindows: boolean | null = null;
  private readonly iosRegex = /iPad|iPhone|iPod/;
  private readonly safariRegex = /Safari/;
  private readonly chromeRegex = /Chrome|CriOS|FxiOS|EdgiOS/;
  private readonly yandexRegex = /YaBrowser|Yandex/;
  private readonly versionRegex = /Version\/(\d+)/;
  private readonly windowsRegex = /Windows NT|Win64|Win32/;
  private colorCache = new Map<string, string>();
  private isBottomBlockHidden = false;
  private currentColor: string = '';
  private isInitialized = false;

  constructor(
    private rendererFactory: RendererFactory2,
    private loadingService: LoadingService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  setColor(color: string, options: {
    useTransparentOverlay?: boolean,
    addBottomSpacing?: boolean
  } = {}): void {
    if (!this.isBrowser) return;

    if (this.isWindows()) {
      return;
    }

    if (this.currentColor === color && this.isInitialized) {
      return;
    }

    const {
      useTransparentOverlay = false,
      addBottomSpacing = true
    } = options;

    const formattedColor = this.formatColor(color);
    this.currentColor = color;

    if (!this.originalBodyBgColor) {
      this.originalBodyBgColor = document.body.style.backgroundColor;
      this.originalHtmlBgColor = document.documentElement.style.backgroundColor;
    }

    if (this.isYandexBrowser()) {
      this.setYandexStyle(formattedColor);
    } else if (this.isMobileSafari()) {
      if (useTransparentOverlay) {
        this.setTransparentOverlayStyle(formattedColor);
      } else {
        this.setSolidColorStyle(formattedColor, addBottomSpacing);
      }
    }

    this.isInitialized = true;
  }

  reset(): void {
    if (!this.isBrowser) return;

    if (this.isWindows() && !this.metaThemeColor && !this.fixIosTop && !this.fixIosBottom) {
      return;
    }

    if (this.metaThemeColor?.parentNode) {
      this.renderer.removeChild(document.head, this.metaThemeColor);
      this.metaThemeColor = null;
    }

    if (this.fixIosTop?.parentNode) {
      this.renderer.removeChild(document.body, this.fixIosTop);
      this.fixIosTop = null;
    }
    if (this.fixIosBottom?.parentNode) {
      this.renderer.removeChild(document.body, this.fixIosBottom);
      this.fixIosBottom = null;
    }

    this.isBottomBlockHidden = false;
    this.isInitialized = false;
    this.currentColor = '';
    document.body.style.backgroundColor = this.originalBodyBgColor;
    document.documentElement.style.backgroundColor = this.originalHtmlBgColor;
  }

  isMobileSafari(): boolean {
    if (this.cachedIsMobileSafari !== null) return this.cachedIsMobileSafari;
    if (!this.isBrowser) return false;

    const ua = navigator.userAgent;
    const isIOS = this.iosRegex.test(ua);
    const isSafari = this.safariRegex.test(ua) && !this.chromeRegex.test(ua) && !this.yandexRegex.test(ua);

    this.cachedIsMobileSafari = isIOS && isSafari;
    return this.cachedIsMobileSafari;
  }

  isYandexBrowser(): boolean {
    if (this.cachedIsYandexBrowser !== null) return this.cachedIsYandexBrowser;
    if (!this.isBrowser) return false;

    const ua = navigator.userAgent;
    this.cachedIsYandexBrowser = this.yandexRegex.test(ua);

    if (this.cachedIsYandexBrowser && this.isWindows()) {
      this.cachedIsYandexBrowser = false;
    }

    return this.cachedIsYandexBrowser;
  }

  isWindows(): boolean {
    if (this.cachedIsWindows !== null) return this.cachedIsWindows;
    if (!this.isBrowser) return false;

    const ua = navigator.userAgent;
    this.cachedIsWindows = this.windowsRegex.test(ua);
    return this.cachedIsWindows;
  }

  isIosVersion(version: number): boolean {
    if (!this.isBrowser || !this.isMobileSafari()) return false;
    if (this.cachedIosVersion === null) {
      const ua = navigator.userAgent;
      const match = ua.match(this.versionRegex);
      this.cachedIosVersion = match && match[1] ? parseInt(match[1], 10) : 0;
    }
    return this.cachedIosVersion === version;
  }

  isIOS(): boolean {
    if (this.cachedIsIOS !== null) return this.cachedIsIOS;
    if (!this.isBrowser) return false;

    this.cachedIsIOS = this.iosRegex.test(navigator.userAgent);
    return this.cachedIsIOS;
  }

  clearCache(): void {
    this.cachedIsMobileSafari = null;
    this.cachedIosVersion = null;
    this.cachedIsIOS = null;
    this.cachedIsYandexBrowser = null;
    this.cachedIsWindows = null;
  }

  setBottomBlockVisibility(hide: boolean): void {
    if (!this.isBrowser || !this.fixIosBottom) return;

    if (this.isBottomBlockHidden === hide) return;

    this.isBottomBlockHidden = hide;

    if (hide) {
      this.renderer.setStyle(this.fixIosBottom, 'display', 'none');
    } else {
      this.renderer.setStyle(this.fixIosBottom, 'display', 'block');
      this.renderer.setStyle(this.fixIosBottom, 'position', 'fixed');
      this.renderer.setStyle(this.fixIosBottom, 'bottom', '0');
      this.renderer.setStyle(this.fixIosBottom, 'left', '0');
      this.renderer.setStyle(this.fixIosBottom, 'width', '100%');
      this.renderer.setStyle(this.fixIosBottom, 'height', '10px');
      this.renderer.setStyle(this.fixIosBottom, 'zIndex', '9999');
      this.renderer.setStyle(this.fixIosBottom, 'pointerEvents', 'none');
    }
  }

  private createOrUpdateMetaTag(color: string): void {
    if (!this.metaThemeColor) {
      this.metaThemeColor = this.renderer.createElement('meta');
      this.renderer.setAttribute(this.metaThemeColor, 'name', 'theme-color');
      this.renderer.appendChild(document.head, this.metaThemeColor);
    }
    this.renderer.setAttribute(this.metaThemeColor, 'content', color);
  }

  private setYandexStyle(color: string): void {
    this.removeFixElements();
    this.createOrUpdateMetaTag(color);
    document.body.style.backgroundColor = color;
    document.body.style.transition = 'background-color 0.3s';
    this.addFixElements(color, true);
  }

  private setSolidColorStyle(color: string, addBottomSpacing: boolean): void {
    this.createOrUpdateMetaTag(color);
    document.body.style.backgroundColor = color;
    document.body.style.transition = 'background-color 0.3s';

    if (!this.isIosVersion(26)) {
      setTimeout(() => {
        if (this.metaThemeColor) {
          this.renderer.setAttribute(this.metaThemeColor, 'content', color);
        }
      });
    }

    if (addBottomSpacing) {
      this.addFixElements(color, false);
    }
  }

  private setTransparentOverlayStyle(color: string): void {
    const computedStyle = getComputedStyle(document.body);
    this.originalHtmlBgColor = computedStyle.backgroundColor;
    document.documentElement.style.backgroundColor = '#a1d5fa';
    document.body.style.backgroundColor = this.hexToRgba(color, 0.5);
  }

  private addFixElements(color: string, bottomOnly: boolean = false): void {
    if (this.fixIosTop || this.fixIosBottom) {
      this.removeFixElements();
    }

    const createFixElement = (position: 'top' | 'bottom'): HTMLDivElement => {
      const el = this.renderer.createElement('div');
      this.renderer.addClass(el, `ios-safari-fix-${position}`);
      this.renderer.setStyle(el, 'backgroundColor', color);
      this.renderer.setStyle(el, 'position', 'fixed');
      this.renderer.setStyle(el, position, '0');
      this.renderer.setStyle(el, 'left', '0');
      this.renderer.setStyle(el, 'width', '100%');
      this.renderer.setStyle(el, 'height', '10px');
      this.renderer.setStyle(el, 'zIndex', '9999');
      this.renderer.setStyle(el, 'pointerEvents', 'none');
      return el;
    };

    if (!bottomOnly) {
      this.fixIosTop = createFixElement('top');
      this.renderer.appendChild(document.body, this.fixIosTop);
    }

    this.fixIosBottom = createFixElement('bottom');
    this.renderer.appendChild(document.body, this.fixIosBottom);

    this.isBottomBlockHidden = false;
  }

  private removeFixElements(): void {
    if (this.fixIosTop?.parentNode) {
      this.renderer.removeChild(document.body, this.fixIosTop);
      this.fixIosTop = null;
    }
    if (this.fixIosBottom?.parentNode) {
      this.renderer.removeChild(document.body, this.fixIosBottom);
      this.fixIosBottom = null;
    }
  }

  private formatColor(col: string): string {
    const cached = this.colorCache.get(col);
    if (cached) return cached;

    let color = col;
    if (this.loadingService.getLoading()) {
      color = '#000000';
      this.colorCache.set(col, color);
      return color;
    }

    if (color.startsWith('#') || color.startsWith('rgb')) {
      this.colorCache.set(col, color);
      return color;
    }

    const hexMatch = color.match(/^([0-9A-Fa-f]{3}){1,2}$/);
    if (hexMatch) {
      color = `#${color}`;
    }

    this.colorCache.set(col, color);
    return color;
  }

  private hexToRgba(hex: string, alpha: number): string {
    const cacheKey = `${hex}:${alpha}`;
    const cached = this.colorCache.get(cacheKey);
    if (cached) return cached;

    let cleanHex = hex.replace('#', '');
    if (cleanHex.length === 3) {
      cleanHex = cleanHex[0] + cleanHex[0] + cleanHex[1] + cleanHex[1] + cleanHex[2] + cleanHex[2];
    }

    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    const result = `rgba(${r}, ${g}, ${b}, ${alpha})`;

    this.colorCache.set(cacheKey, result);
    return result;
  }
}
